// 엑셀의 공종 이름을 회사 공종 코드에 짝짓는다 — 이름 일치 → 포함 → 동의어 순.

import { tradeKeyOf } from "./rows";
import { ImportGroup } from "./types";
import { ImportTradeInput } from "../editor/types";
import { TradeGroup } from "../../template/trades";

export interface TradeTarget {
  code: string;
  name: string;
}

// 내장 공종 코드별로 엑셀에서 흔히 쓰는 말.
const SYNONYMS: [string, string[]][] = [
  ["demolition", ["철거", "해체", "폐기물"]],
  ["plumbing", ["설비", "배관", "수도", "난방", "보일러"]],
  ["carpentry", ["목공", "목작업", "목수", "가벽", "천장"]],
  ["electric", ["전기", "조명", "배선", "콘센트"]],
  ["tile", ["타일", "조적", "방수"]],
  ["film", ["필름", "시트지"]],
  ["paint", ["도장", "페인트", "탄성"]],
  ["wallpaper", ["도배", "벽지"]],
  ["window", ["창호", "샷시", "샤시", "새시", "창문"]],
  ["floor", ["마루", "바닥", "장판"]],
  ["door", ["도어", "중문", "방문", "현관문"]],
  ["bath", ["욕실", "바스", "위생", "도기", "수전"]],
  ["furniture", ["가구", "주방", "싱크", "붙박이", "신발장"]],
  ["docs", ["보양", "서류", "가설", "행정"]],
  ["finish", ["마감", "청소", "준공", "실리콘", "잡공사", "기타"]],
];

/** 엑셀 공종 이름 → 공종 코드. 못 찾으면 null. */
export const matchTrade = (name: string, targets: TradeTarget[]): string | null => {
  const key = tradeKeyOf(name);
  if (key === "") return null;
  const keyed = targets.map((t) => ({ code: t.code, key: tradeKeyOf(t.name) }));
  const exact = keyed.find((t) => t.key === key);
  if (exact) return exact.code;
  const partial = keyed.find(
    (t) => t.key.length >= 2 && key.length >= 2 && (t.key.includes(key) || key.includes(t.key))
  );
  if (partial) return partial.code;
  const codes = new Set(targets.map((t) => t.code));
  for (const [code, words] of SYNONYMS) {
    if (codes.has(code) && words.some((w) => key.includes(w))) return code;
  }
  return null;
};

export interface TradeOption extends TradeTarget {
  group: TradeGroup;
  sort_order: number;
}

/** 짝짓기 값 — 공종 코드, 「담지 않음」(SKIP), 아직 안 고름(""). */
export const SKIP_TRADE = "__skip__";

/** 공종 짝짓기 결과로 리듀서에 보낼 덩이를 만든다. 같은 코드로 짝지은 덩이는 순서대로 합친다. */
export const buildImportTrades = (
  groups: ImportGroup[],
  assign: Record<string, string>,
  options: TradeOption[]
): ImportTradeInput[] => {
  const byCode = new Map(options.map((o) => [o.code, o]));
  const out = new Map<string, ImportTradeInput>();
  for (const group of groups) {
    const option = byCode.get(assign[group.name] ?? "");
    if (!option || group.items.length === 0) continue;
    const found = out.get(option.code);
    if (found) {
      found.items.push(...group.items);
      continue;
    }
    out.set(option.code, {
      trade: option.code,
      name: option.name,
      group: option.group,
      sort_order: option.sort_order,
      items: [...group.items],
    });
  }
  return [...out.values()];
};
